import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Minus, Plus } from "lucide-react";

export function ProductDetailModal({ product, onClose, onAddToOrder, currentOrder, onAddProductDetailToOrder, initialDealSelections,
  initialOptionQuantities, initialQuantity, initialSpecialInstructions }) {
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedFlavour, setSelectedFlavour] = useState("");
  const [specialInstructions, setSpecialInstructions] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [productVariants, setProductVariants] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [flavours, setFlavours] = useState([]);
  const [dealInfo, setDealInfo] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [optionQuantities, setOptionQuantities] = useState({});
  const [totalPrice, setTotalPrice] = useState(0);
  // Store deal selections per main-product quantity index (1-based) for multi-select checkbox deals
  const [perQuantitySelections, setPerQuantitySelections] = useState({});
  // Store radio (single-selection) deal options per main-product quantity index
  const [perQuantityRadioSelections, setPerQuantityRadioSelections] = useState({});
  const [activeQuantityTab, setActiveQuantityTab] = useState(1);
  const [discountPercent, setDiscountPercent] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [basePrice, setBasePrice] = useState(0);

  useEffect(() => {
    if (product.ProductPrice) {
      setBasePrice(parseFloat(product.ProductPrice));
    }
  }, [product]);

  // Initialize with existing selections when editing an item
  useEffect(() => {
    if (initialQuantity) {
      setQuantity(initialQuantity);
    }
    if (initialSpecialInstructions !== undefined) {
      setSpecialInstructions(initialSpecialInstructions || "");
    }
  }, [initialQuantity, initialSpecialInstructions]);

  // Restore deal selections when initialDealSelections is provided
  useEffect(() => {
    if (initialDealSelections && Object.keys(initialDealSelections).length > 0 && dealInfo) {
      const restoredSelectedOptions = {};
      const restoredPerQuantitySelections = {};
      const restoredPerQuantityRadioSelections = {};

      // Determine if we're dealing with single or multiple quantities
      const itemQuantity = initialQuantity || 1;
      // When editing, we restore to the first quantity tab (qtyIndex = 1)
      // This represents the item being edited
      const qtyIndex = 1;

      Object.entries(initialDealSelections).forEach(([dealItemId, selection]) => {
        // Find the deal item to determine if it's radio or checkbox
        const dealItem = dealInfo.find(di => di.DealItemId.toString() === dealItemId.toString());
        if (!dealItem) return;

        const dealDescriptions = dealItem.DealDescription || [];
        const perItemMax = dealItem.MaxQuantity === 0 ? Infinity : dealItem.MaxQuantity;
        const isRadioSelection = perItemMax === 1;

        // Helper function to map ProductDetailId to DealDescId
        const findDealDescId = (productDetailId) => {
          const desc = dealDescriptions.find(d =>
            d.ProductDetailId && d.ProductDetailId.toString() === productDetailId.toString()
          );
          return desc ? desc.DealDescId : productDetailId; // Fallback to productDetailId if not found
        };

        if (isRadioSelection) {
          // Radio selection (single object)
          if (selection && !Array.isArray(selection)) {
            // Map ProductDetailId to DealDescId if needed (for recalled orders)
            const mappedSelection = {
              ...selection,
              id: findDealDescId(selection.id || selection.productDetailId)
            };

            if (itemQuantity > 1) {
              // For multiple quantities, restore to perQuantityRadioSelections structure
              if (!restoredPerQuantityRadioSelections[qtyIndex]) {
                restoredPerQuantityRadioSelections[qtyIndex] = {};
              }
              restoredPerQuantityRadioSelections[qtyIndex][dealItemId] = mappedSelection;

              // Also set for other quantity tabs if they should have the same selection
              // (This is a reasonable default when editing)
              for (let i = 2; i <= itemQuantity; i++) {
                if (!restoredPerQuantityRadioSelections[i]) {
                  restoredPerQuantityRadioSelections[i] = {};
                }
                restoredPerQuantityRadioSelections[i][dealItemId] = mappedSelection;
              }
            } else {
              // Single quantity: use selectedOptions (legacy structure)
              restoredSelectedOptions[dealItemId] = mappedSelection;
            }
          }
        } else {
          // Checkbox selection (array) - always goes to perQuantitySelections structure
          if (Array.isArray(selection) && selection.length > 0) {
            // Initialize the quantity tab structure if it doesn't exist
            if (!restoredPerQuantitySelections[qtyIndex]) {
              restoredPerQuantitySelections[qtyIndex] = {
                selectedOptions: {},
                optionQuantities: {}
              };
            }

            // Map ProductDetailId to DealDescId for each option (for recalled orders)
            const mappedSelection = selection.map(opt => ({
              ...opt,
              id: findDealDescId(opt.id || opt.productDetailId)
            }));

            // Restore checkbox selections to perQuantitySelections[qtyIndex].selectedOptions
            restoredPerQuantitySelections[qtyIndex].selectedOptions[dealItemId] = mappedSelection;

            // Restore option quantities to perQuantitySelections[qtyIndex].optionQuantities
            // Need to map the keys from ProductDetailId-based to DealDescId-based
            if (initialOptionQuantities) {
              mappedSelection.forEach(opt => {
                // Try both old key (ProductDetailId) and new key (DealDescId)
                const oldKey = `${dealItemId}-${opt.productDetailId || opt.id}`;
                const newKey = `${dealItemId}-${opt.id}`;

                let qty = 1;
                if (initialOptionQuantities[newKey]) {
                  qty = initialOptionQuantities[newKey];
                } else if (initialOptionQuantities[oldKey]) {
                  qty = initialOptionQuantities[oldKey];
                }

                restoredPerQuantitySelections[qtyIndex].optionQuantities[newKey] = qty;
              });
            } else {
              // If no optionQuantities provided, default all to 1
              mappedSelection.forEach(opt => {
                const key = `${dealItemId}-${opt.id}`;
                restoredPerQuantitySelections[qtyIndex].optionQuantities[key] = 1;
              });
            }
          }
        }
      });

      // Apply restored selections
      if (Object.keys(restoredSelectedOptions).length > 0) {
        setSelectedOptions(restoredSelectedOptions);
      }
      if (Object.keys(restoredPerQuantitySelections).length > 0) {
        setPerQuantitySelections(restoredPerQuantitySelections);
      }
      if (Object.keys(restoredPerQuantityRadioSelections).length > 0) {
        setPerQuantityRadioSelections(restoredPerQuantityRadioSelections);
      }
    }
  }, [initialDealSelections, initialOptionQuantities, dealInfo, initialQuantity]);

  useEffect(() => {

    // Handle deal information first
    if (currentOrder && currentOrder.Data && currentOrder.Data.Deals && product.IsDeal) {

      // First, find the ProductDetailId for this product from the Table array
      const tableProduct = currentOrder.Table.find(item =>
        item.ProductId === product.ProductId
      );

      if (tableProduct && tableProduct.ProductDetailId) {
        const dealItems = currentOrder.Data.Deals.DealItemDetail || [];

        // // console.log(dealItems)

        // Find deal items that match this ProductDetailId
        const matchingDealItems = dealItems.filter(item =>
          item.ProductDetailId === tableProduct.ProductDetailId
        );

        if (matchingDealItems.length > 0) {
          setDealInfo(matchingDealItems);
          setProductVariants([]);
          setSizes([]);
          setFlavours([]);

          // Set the base price from the table product
          // setBasePrice(parseFloat(tableProduct.Price || product.ProductPrice));
          const selectedVariant = getSelectedVariant();
          const variantPrice = selectedVariant ? parseFloat(selectedVariant.Price) : basePrice;
          const priceBeforeDiscount = selectedVariant ? parseFloat(selectedVariant.PriceBeforeDiscount) : basePrice;
        }

        if (matchingDealItems.length === 0) {
          setDealInfo(matchingDealItems);
        }
      } else {
        // Fallback if ProductDetailId not found in Table
        console.warn("ProductDetailId not found in Table array, using product data");
        const dealItems = currentOrder.Data.Deals.DealItemDetail || [];
        const matchingDealItems = dealItems.filter(item =>
          item.ProductId === product.ProductId
        );

        if (matchingDealItems.length > 0) {
          setDealInfo(matchingDealItems);
          setProductVariants([]);
          setSizes([]);
          setFlavours([]);
        }
      }
      return;
    }

    // Handle product variants (only if NOT a deal)
    if (currentOrder && currentOrder.Table6 && !product.IsDeal) {
      const variants = currentOrder.Table6.filter(
        item => item.ProductId === product.ProductId
      );

      if (variants.length > 0) {
        setProductVariants(variants);

        const uniqueSizes = [...new Map(variants.map(item =>
          [item.SizeId, { SizeId: item.SizeId, SizeName: item.SizeName }]
        )).values()];
        setSizes(uniqueSizes);

        const uniqueFlavours = [...new Map(variants.map(item =>
          [item.FlavourId, { FlavourId: item.FlavourId, FlavourName: item.FlavourName }]
        )).values()];
        setFlavours(uniqueFlavours);

        if (uniqueSizes.length > 0) {
          setSelectedSize(uniqueSizes[0].SizeId.toString());
        }
        if (uniqueFlavours.length > 0) {
          setSelectedFlavour(uniqueFlavours[0].FlavourId.toString());
        }
      }
    }
  }, [product, currentOrder]);

  useEffect(() => {
    if (currentOrder && currentOrder.Table14 && productVariants.length > 0) {
      // Find discount for the selected variant
      const selectedVariant = getSelectedVariant();
      if (selectedVariant && selectedVariant.ProductDetailId) {
        const discount = currentOrder.Table14.find(
          d => d.ProductDetailId === selectedVariant.ProductDetailId
        );

        if (discount) {
          setDiscountPercent(discount.DiscountPercent || 0);
        } else {
          setDiscountPercent(0);
        }
      }
    }
  }, [productVariants, selectedSize, selectedFlavour, currentOrder]);

  // Keep base price aligned with the variant for the selected size/flavour (size radio at ~774)
  useEffect(() => {
    if (productVariants.length === 0) return;
    const variant = productVariants.find(
      v =>
        v.SizeId.toString() === selectedSize &&
        v.FlavourId.toString() === selectedFlavour
    );
    if (variant) {
      setBasePrice(parseFloat(variant.Price));
    }
  }, [selectedSize, selectedFlavour, productVariants]);

  useEffect(() => {
    calculateTotalPrice();
  }, [selectedOptions, perQuantitySelections, quantity, selectedSize, selectedFlavour, discountPercent, basePrice, dealInfo]);

  // Keep activeQuantityTab within range when main quantity changes
  useEffect(() => {
    if (quantity < 1) {
      setActiveQuantityTab(1);
      return;
    }
    setActiveQuantityTab(prev => {
      if (prev < 1) return 1;
      if (prev > quantity) return quantity;
      return prev;
    });
  }, [quantity]);

  const handleRadioChange = (dealItemId, dealDescId, dealOption) => {
    const selection = {
      id: dealDescId,
      productDetailId: dealOption.ProductDetailId,
      name: dealOption.ProductDetailName || dealOption.SizeName,
      price: dealOption.Price || 0
    };
    const selectionArray = [selection];

    // When main product quantity is more than 1, keep radio selection per item (per active tab)
    if (quantity > 1) {
      const qtyIndex = activeQuantityTab || 1;
      setPerQuantityRadioSelections(prev => ({
        ...prev,
        [qtyIndex]: {
          ...(prev[qtyIndex] || {}),
          [dealItemId]: selectionArray
        }
      }));
    } else {
      // Single quantity: keep legacy behavior (one common radio selection map)
      setSelectedOptions(prev => ({
        ...prev,
        [dealItemId]: selectionArray
      }));
    }
  };

  // Checkbox selection per main-product quantity section
  const handleCheckboxChange = (qtyIndex, dealItemId, dealDescId, dealOption, checked) => {
    setPerQuantitySelections(prev => {
      const qtyState = prev[qtyIndex] || { selectedOptions: {}, optionQuantities: {} };
      const currentSelection = qtyState.selectedOptions[dealItemId] || [];
      const optionData = {
        id: dealDescId,
        productDetailId: dealOption.ProductDetailId,
        name: dealOption.ProductDetailName || dealOption.SizeName,
        price: dealOption.Price || 0
      };

      let newSelectedForDeal;
      let newOptionQuantities = { ...qtyState.optionQuantities };

      if (checked) {
        newSelectedForDeal = [...currentSelection, optionData];
        // Ensure at least quantity 1 when first selected
        const key = `${dealItemId}-${dealDescId}`;
        if (!newOptionQuantities[key]) {
          newOptionQuantities[key] = 1;
        }
      } else {
        const key = `${dealItemId}-${dealDescId}`;
        delete newOptionQuantities[key];
        newSelectedForDeal = currentSelection.filter(opt => opt.id !== dealDescId);
      }

      const newSelectedOptions = {
        ...qtyState.selectedOptions,
        [dealItemId]: newSelectedForDeal
      };

      return {
        ...prev,
        [qtyIndex]: {
          selectedOptions: newSelectedOptions,
          optionQuantities: newOptionQuantities
        }
      };
    });
  };

  const handleOptionQuantityChange = (qtyIndex, dealItemId, dealDescId, newQuantity) => {
    const safeQty = Math.max(1, newQuantity);
    setPerQuantitySelections(prev => {
      const qtyState = prev[qtyIndex] || { selectedOptions: {}, optionQuantities: {} };
      const newOptionQuantities = {
        ...qtyState.optionQuantities,
        [`${dealItemId}-${dealDescId}`]: safeQty
      };

      return {
        ...prev,
        [qtyIndex]: {
          ...qtyState,
          optionQuantities: newOptionQuantities
        }
      };
    });
  };

  // Get the selected variant based on size and flavour
  const getSelectedVariant = () => {

    const variant = productVariants.find(variant =>
      variant.SizeId.toString() === selectedSize &&
      variant.FlavourId.toString() === selectedFlavour
    );

    return variant;
  };

  const selectedVariant = getSelectedVariant();
  const priceBeforeDiscount = selectedVariant ? parseFloat(selectedVariant.PriceBeforeDiscount) : 0;

  const calculateTotalPrice = () => {
    // Sum price per main-product quantity (tab)
    let totalBeforeDiscount = 0;
    let totalAfterDiscount = 0;

    for (let i = 1; i <= quantity; i++) {
      let itemPrice = basePrice;

      // Add radio (single-selection) deal options
      if (dealInfo) {
        dealInfo.forEach(dealItem => {
          const perItemMax = dealItem.MaxQuantity === 0 ? Infinity : dealItem.MaxQuantity;
          const isRadioSelection = perItemMax === 1;
          if (!isRadioSelection) return;

          // For qty > 1, use per-quantity radio selection; otherwise use global selection
          const selection = quantity > 1
            ? (perQuantityRadioSelections[i]?.[dealItem.DealItemId] || null)
            : selectedOptions[dealItem.DealItemId];
          const radioSelection = Array.isArray(selection) ? selection[0] : selection;
          if (radioSelection && typeof radioSelection === "object") {
            itemPrice += parseFloat(radioSelection.price) || 0;
          }
        });
      }

      // Add checkbox (multi-select) options for this quantity
      const qtyState = perQuantitySelections[i] || { selectedOptions: {}, optionQuantities: {} };
      const { selectedOptions: qtyOptions = {}, optionQuantities: qtyOptionQuantities = {} } = qtyState;

      Object.entries(qtyOptions).forEach(([dealItemId, selection]) => {
        if (Array.isArray(selection)) {
          selection.forEach(opt => {
            const key = `${dealItemId}-${opt.id}`;
            const optQty = qtyOptionQuantities[key] || 1;
            itemPrice += (parseFloat(opt.price) || 0) * optQty;
          });
        }
      });

      totalBeforeDiscount += itemPrice;

      const discountAmt = discountPercent > 0 ? (itemPrice * discountPercent / 100) : 0;
      totalAfterDiscount += (itemPrice - discountAmt);
    }

    setDiscountAmount(totalBeforeDiscount - totalAfterDiscount);
    setTotalPrice(totalAfterDiscount);
  };

  const areDealSelectionsEqual = (sel1, sel2) => {
    const keys1 = Object.keys(sel1);
    const keys2 = Object.keys(sel2);

    if (keys1.length !== keys2.length) return false;

    for (let key of keys1) {
      // For radio selections (single objects)
      if (sel1[key] && !Array.isArray(sel1[key]) && sel2[key] && !Array.isArray(sel2[key])) {
        if (sel1[key].id !== sel2[key].id) return false;
      }
      // For checkbox selections (arrays)
      else if (Array.isArray(sel1[key]) && Array.isArray(sel2[key])) {
        if (sel1[key].length !== sel2[key].length) return false;

        const sorted1 = [...sel1[key]].sort((a, b) => a.id - b.id);
        const sorted2 = [...sel2[key]].sort((a, b) => a.id - b.id);

        for (let i = 0; i < sorted1.length; i++) {
          if (sorted1[i].id !== sorted2[i].id) return false;
        }
      } else {
        return false;
      }
    }

    return true;
  };

  const handleAddToCart = () => {

    let productDetailId = null;

    if (currentOrder && currentOrder.Table) {
      const tableProduct = currentOrder.Table.find(
        item => item.ProductId === product.ProductId && item.SizeId == selectedSize && item.FlavourId == selectedFlavour
      );
      if (tableProduct) {
        productDetailId = tableProduct.ProductDetailId;
      }
    }

    // if (!productDetailId) {
    //   productDetailId = product.ProductDetailId || null;
    // }

    if (productDetailId === null) {
      productDetailId = dealInfo && dealInfo.length > 0 ? dealInfo[0].ProductDetailId : (product.ProductDetailId || null);
    }

    const ProductDetailModal_ProductArr = [];

    if (dealInfo && dealInfo.length > 0) {
      for (let i = 1; i <= quantity; i++) {
        let itemPrice = basePrice;

        // Radio selections (per quantity when quantity > 1)
        if (dealInfo) {
          dealInfo.forEach(dealItem => {
            const perItemMax = dealItem.MaxQuantity === 0 ? Infinity : dealItem.MaxQuantity;
            const isRadioSelection = perItemMax === 1;
            if (!isRadioSelection) return;

            const selection = quantity > 1
              ? (perQuantityRadioSelections[i]?.[dealItem.DealItemId] || null)
              : selectedOptions[dealItem.DealItemId];
            const radioSelection = Array.isArray(selection) ? selection[0] : selection;
            if (radioSelection && typeof radioSelection === "object") {
              itemPrice += parseFloat(radioSelection.price) || 0;
            }
          });
        }

        const qtyState = perQuantitySelections[i] || { selectedOptions: {}, optionQuantities: {} };
        const { selectedOptions: qtyOptions = {}, optionQuantities: qtyOptionQuantities = {} } = qtyState;

        Object.entries(qtyOptions).forEach(([dealItemId, selection]) => {
          if (Array.isArray(selection)) {
            selection.forEach(opt => {
              const key = `${dealItemId}-${opt.id}`;
              const optQty = qtyOptionQuantities[key] || 1;
              itemPrice += (parseFloat(opt.price) || 0) * optQty;
            });
          }
        });

        const discountAmt = discountPercent > 0 ? (itemPrice * discountPercent / 100) : 0;
        const finalPrice = itemPrice - discountAmt;

        const itemDetails = {
          id: Date.now() + i,
          ProductId: product.ProductId,
          ProductDetailId: productDetailId,
          name: product.ProductName,
          price: parseFloat(finalPrice),
          basePrice: parseFloat(basePrice),
          PriceBeforeDiscount: parseFloat(priceBeforeDiscount),
          FlavourId: selectedVariant?.FlavourId || null,
          FlavourName: selectedVariant?.FlavourName || null,
          SizeId: selectedVariant?.SizeId || null,
          SizeName: selectedVariant?.SizeName || null,
          GSTPercentage: parseFloat(selectedVariant?.GSTPercentage || product.GSTPercentage || 0),
          description: selectedVariant?.ProductDescription || product.ProductDescription,
          image: selectedVariant?.ProductImage || product.ProductImage,
          SpecialInstructions: specialInstructions,
          quantity: 1,
          discountPercent: discountPercent,
          discountAmount: discountAmt,
          // For this tab, combine radio selections (if any) with checkbox selections
          dealSelections: JSON.parse(JSON.stringify({
            ...selectedOptions,
            ...qtyOptions
          })),
          optionQuantities: JSON.parse(JSON.stringify({
            ...optionQuantities,
            ...qtyOptionQuantities
          })),
          DepartmentId: product.DepartmentId || null,
          DepartmentName: product.DepartmentName || null,
        };

        // onAddToOrder(itemDetails);

        ProductDetailModal_ProductArr.push(itemDetails);
      }
    }
    else {
      for (let i = 1; i <= 1; i++) {
        let itemPrice = basePrice;

        const discountAmt = discountPercent > 0 ? (itemPrice * discountPercent / 100) : 0;
        const finalPrice = itemPrice - discountAmt;

        const itemDetails = {
          id: Date.now() + i,
          ProductId: product.ProductId,
          ProductDetailId: productDetailId,
          name: product.ProductName,
          price: parseFloat(finalPrice),
          basePrice: parseFloat(basePrice),
          PriceBeforeDiscount: parseFloat(priceBeforeDiscount),
          FlavourId: selectedVariant?.FlavourId || null,
          FlavourName: selectedVariant?.FlavourName || null,
          SizeId: selectedVariant?.SizeId || null,
          SizeName: selectedVariant?.SizeName || null,
          GSTPercentage: parseFloat(selectedVariant?.GSTPercentage || product.GSTPercentage || 0),
          description: selectedVariant?.ProductDescription || product.ProductDescription,
          image: selectedVariant?.ProductImage || product.ProductImage,
          SpecialInstructions: specialInstructions,
          quantity: quantity || 1,
          discountPercent: discountPercent,
          discountAmount: discountAmt,
          DepartmentId: product.DepartmentId || null,
          DepartmentName: product.DepartmentName || null,
        };

        // onAddToOrder(itemDetails);

        ProductDetailModal_ProductArr.push(itemDetails);
      }
    }

    // for (let i = 1; i <= quantity; i++) {
    //   let itemPrice = basePrice;

    //   // Radio selections (per quantity when quantity > 1)
    //   if (dealInfo) {
    //     dealInfo.forEach(dealItem => {
    //       const perItemMax = dealItem.MaxQuantity === 0 ? Infinity : dealItem.MaxQuantity;
    //       const isRadioSelection = perItemMax === 1;
    //       if (!isRadioSelection) return;

    //       const selection = quantity > 1
    //         ? (perQuantityRadioSelections[i]?.[dealItem.DealItemId] || null)
    //         : selectedOptions[dealItem.DealItemId];
    //       if (selection && typeof selection === "object" && !Array.isArray(selection)) {
    //         itemPrice += parseFloat(selection.price) || 0;
    //       }
    //     });
    //   }

    //   const qtyState = perQuantitySelections[i] || { selectedOptions: {}, optionQuantities: {} };
    //   const { selectedOptions: qtyOptions = {}, optionQuantities: qtyOptionQuantities = {} } = qtyState;

    //   Object.entries(qtyOptions).forEach(([dealItemId, selection]) => {
    //     if (Array.isArray(selection)) {
    //       selection.forEach(opt => {
    //         const key = `${dealItemId}-${opt.id}`;
    //         const optQty = qtyOptionQuantities[key] || 1;
    //         itemPrice += (parseFloat(opt.price) || 0) * optQty;
    //       });
    //     }
    //   });

    //   const discountAmt = discountPercent > 0 ? (itemPrice * discountPercent / 100) : 0;
    //   const finalPrice = itemPrice - discountAmt;

    //   const itemDetails = {
    //     id: Date.now() + i,
    //     ProductId: product.ProductId,
    //     ProductDetailId: productDetailId,
    //     name: product.ProductName,
    //     price: parseFloat(finalPrice),
    //     basePrice: parseFloat(basePrice),
    //     PriceBeforeDiscount: parseFloat(priceBeforeDiscount),
    //     FlavourId: selectedVariant?.FlavourId || null,
    //     FlavourName: selectedVariant?.FlavourName || null,
    //     SizeId: selectedVariant?.SizeId || null,
    //     SizeName: selectedVariant?.SizeName || null,
    //     GSTPercentage: parseFloat(selectedVariant?.GSTPercentage || product.GSTPercentage || 0),
    //     description: selectedVariant?.ProductDescription || product.ProductDescription,
    //     image: selectedVariant?.ProductImage || product.ProductImage,
    //     SpecialInstructions: specialInstructions,
    //     quantity: 1,
    //     discountPercent: discountPercent,
    //     discountAmount: discountAmt,
    //     // For this tab, combine radio selections (if any) with checkbox selections
    //     dealSelections: JSON.parse(JSON.stringify({
    //       ...selectedOptions,
    //       ...qtyOptions
    //     })),
    //     optionQuantities: JSON.parse(JSON.stringify({
    //       ...optionQuantities,
    //       ...qtyOptionQuantities
    //     })),
    //     DepartmentId: product.DepartmentId || null,
    //     DepartmentName: product.DepartmentName || null,
    //   };

    //   // onAddToOrder(itemDetails);

    //   ProductDetailModal_ProductArr.push(itemDetails);
    // }

    onAddProductDetailToOrder(ProductDetailModal_ProductArr);
  };

  const increaseQuantity = () => {
    setQuantity(prev => prev + 1);
  };

  const decreaseQuantity = () => {
    if (quantity > 1) {
      setQuantity(prev => prev - 1);
    }
  };

  const increaseOptionQuantity = (dealItemId, dealDescId) => {
    // This function is now unused for checkbox deals (handled per quantity)
  };

  const decreaseOptionQuantity = (dealItemId, dealDescId) => {
    // This function is now unused for checkbox deals (handled per quantity)
  };

  const isDealSelectionValid = () => {
    if (!dealInfo) return true;

    for (const dealItem of dealInfo) {
      const perItemMin = dealItem.Quantity || 0;
      const perItemMax = dealItem.MaxQuantity === 0 ? Infinity : dealItem.MaxQuantity;
      const isRadioSelection = perItemMax === 1;

      if (isRadioSelection) {
        // For radio deals, ensure required selections per item
        if (quantity > 1) {
          // Each main-product quantity (item tab) must have a selection if min > 0
          if (perItemMin > 0) {
            for (let i = 1; i <= quantity; i++) {
              const selectionForItem = perQuantityRadioSelections[i]?.[dealItem.DealItemId] || null;
              const hasSelectionForItem = Array.isArray(selectionForItem)
                ? selectionForItem.length > 0
                : !!selectionForItem;
              if (!hasSelectionForItem) {
                return false;
              }
            }
          }
        } else {
          const selection = selectedOptions[dealItem.DealItemId] || null;
          const hasSelection = Array.isArray(selection) ? selection.length > 0 : !!selection;
          if (perItemMin > 0 && !hasSelection) {
            return false;
          }
        }

        // No further checks needed for radio items
        continue;
      }

      // Multi-select (checkbox) validation - validate per quantity tab
      // Each quantity tab must independently meet the per-item min/max requirements
      for (let i = 1; i <= quantity; i++) {
        const qtyState = perQuantitySelections[i] || { selectedOptions: {}, optionQuantities: {} };
        const qtySelections = qtyState.selectedOptions[dealItem.DealItemId] || [];
        const qtyOptionQuantities = qtyState.optionQuantities || {};

        // Calculate total quantity of selected options for this quantity tab
        let totalQuantityForQty = 0;
        if (Array.isArray(qtySelections)) {
          qtySelections.forEach(opt => {
            const key = `${dealItem.DealItemId}-${opt.id}`;
            const optQty = qtyOptionQuantities[key] || 1;
            totalQuantityForQty += optQty;
          });
        }

        // Validate min requirement per item
        if (totalQuantityForQty < perItemMin) {
          return false;
        }

        // Validate max requirement per item
        if (perItemMax !== Infinity && totalQuantityForQty > perItemMax) {
          return false;
        }
      }
    }

    return true;
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent
        className={`${dealInfo?.length > 3 ? 'max-w-[90vw]' : 'max-w-5xl'
          } max-h-[95vh] flex flex-col p-0`}
      >
        <DialogHeader className="px-6 items-center py-4 border-b">
          <DialogTitle className="text-2xl font-semibold">
            {product.ProductName}
          </DialogTitle>
          <p className="text-muted-foreground text-sm">{product.ProductDescription}</p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 pb-2">
          <div className="grid grid-cols-1 md:grid-cols-1">
            {/* Product Details */}
            <div className="space-y-4">
              {/* Size Selection */}
              {sizes.length > 0 && (
                <div className="space-y-3">
                  <Label className="text-base font-medium">Size</Label>
                  <RadioGroup value={selectedSize} onValueChange={setSelectedSize}>
                    {sizes.map((size) => {
                      const variant = productVariants.find(v =>
                        v.SizeId === size.SizeId &&
                        v.FlavourId.toString() === selectedFlavour
                      );
                      const variantPrice = variant ? variant.Price : 0;
                      return (
                        <div key={size.SizeId} className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-muted/50">
                          <RadioGroupItem value={size.SizeId.toString()} id={`size-${size.SizeId}`} />
                          <Label htmlFor={`size-${size.SizeId}`} className="flex-1 cursor-pointer">
                            <div className="flex justify-between items-center">
                              <span>{size.SizeName}</span>
                              <span className="font-semibold">Rs {variantPrice.toFixed(2)}</span>
                            </div>
                          </Label>
                        </div>
                      );
                    })}
                  </RadioGroup>
                </div>
              )}
            </div>

            {/* Deal Options */}
            {dealInfo && (
              <div className={`grid gap-2 ${dealInfo.length > 4 ? 'md:grid-cols-4' : `md:grid-cols-${dealInfo.length}`}`}>
                {dealInfo.map(dealItem => {
                  const dealDescriptions = dealItem.DealDescription || [];
                  const descriptionsForThisItem = dealDescriptions.filter(desc =>
                    desc.DealItemId === dealItem.DealItemId
                  );

                  // Determine selection constraints
                  const minSelections = dealItem.Quantity || 0;
                  const maxSelections = dealItem.MaxQuantity === 0 ? Infinity : dealItem.MaxQuantity;
                  const isRadioSelection = maxSelections === 1;

                  // For radio selections, selection can be per quantity (item tab) when quantity > 1
                  const currentSelections = selectedOptions[dealItem.DealItemId] ||
                    (isRadioSelection ? null : []);
                  const currentCount = Array.isArray(currentSelections) ?
                    currentSelections.length :
                    (currentSelections ? 1 : 0);

                  // Calculate total quantity across all selected options for validation messages (scaled later)
                  let totalQuantity = 0;
                  if (Array.isArray(currentSelections)) {
                    currentSelections.forEach(opt => {
                      const qty = optionQuantities[`${dealItem.DealItemId}-${opt.id}`] || 1;
                      totalQuantity += qty;
                    });
                  } else if (currentSelections) {
                    totalQuantity = optionQuantities[`${dealItem.DealItemId}-${currentSelections.id}`] || 1;
                  }

                  // Check if we've reached the maximum total quantity across all quantities
                  const hasReachedMaxQuantity = maxSelections === Infinity ? false : (totalQuantity >= maxSelections * quantity);

                  return (
                    <div key={dealItem.DealItemId} className="space-y-3 p-4 border rounded-lg">
                      <Label className="text-base font-medium">
                        <span className="font-bold">{dealItem.DealOptionName}</span>
                        <span className="text-xs text-muted-foreground ml-2 block mt-1">
                          {minSelections > 0 && `Select at least ${minSelections}`}
                          {minSelections > 0 && maxSelections !== Infinity && ', '}
                          {maxSelections !== Infinity && `up to ${maxSelections}`}
                          {minSelections > 0 && maxSelections !== Infinity && minSelections === maxSelections &&
                            ` (exactly ${minSelections})`}
                        </span>
                      </Label>

                      {/* Radio buttons for single selection cases */}
                      {isRadioSelection && (
                        <div className="space-y-3">
                          {/* Item tabs when main product quantity > 1 */}
                          {quantity > 1 && (
                            <div className="flex flex-wrap gap-2 mb-1">
                              {Array.from({ length: quantity }, (_, idx) => {
                                const tabIndex = idx + 1;
                                const isActive = activeQuantityTab === tabIndex;
                                return (
                                  <Button
                                    key={tabIndex}
                                    variant={isActive ? "default" : "outline"}
                                    size="xs"
                                    className={`h-7 px-3 text-xs ${isActive ? "" : "bg-background"}`}
                                    onClick={() => setActiveQuantityTab(tabIndex)}
                                  >
                                    Item {tabIndex}
                                  </Button>
                                );
                              })}
                            </div>
                          )}

                          {/* Radio options (selection per item when qty > 1) */}
                          <RadioGroup
                            value={
                              quantity > 1
                                ? (Array.isArray(perQuantityRadioSelections[activeQuantityTab]?.[dealItem.DealItemId])
                                  ? (perQuantityRadioSelections[activeQuantityTab]?.[dealItem.DealItemId]?.[0]?.id || "")
                                  : (perQuantityRadioSelections[activeQuantityTab]?.[dealItem.DealItemId]?.id || ""))
                                : (Array.isArray(currentSelections)
                                  ? (currentSelections?.[0]?.id || "")
                                  : (currentSelections?.id || ""))
                            }
                            onValueChange={(value) => {
                              const selectedDesc = descriptionsForThisItem.find(
                                desc => desc.DealDescId.toString() === value
                              );
                              handleRadioChange(dealItem.DealItemId, value, selectedDesc);
                            }}>
                            {descriptionsForThisItem.map(desc => (
                              <div
                                key={desc.DealDescId}
                                className="flex items-center space-x-2 p-2 border rounded-lg hover:bg-muted/50">
                                <RadioGroupItem
                                  value={desc.DealDescId.toString()}
                                  id={`deal-${desc.DealDescId}`}
                                  required={minSelections > 0}
                                />
                                <Label htmlFor={`deal-${desc.DealDescId}`} className="flex-1 cursor-pointer">
                                  <div className="flex justify-between items-center">
                                    {/* <span className="text-sm">{desc.SizeName || desc.ProductDetailName}</span> */}
                                    <span className="text-sm">{desc.ProductDetailName}</span>

                                    <span className="font-semibold text-sm">
                                      {desc.Price > 0 ? `+ Rs ${desc.Price.toFixed(2)}` : "Included"}
                                    </span>
                                  </div>
                                </Label>
                              </div>
                            ))}
                          </RadioGroup>
                        </div>
                      )}

                      {/* Checkboxes for multiple selection cases - per main product quantity (tabbed) */}
                      {!isRadioSelection && (
                        <div className="space-y-3">
                          {/* Quantity tabs */}
                          {quantity > 1 && (
                            <div className="flex flex-wrap gap-2 mb-1">
                              {Array.from({ length: quantity }, (_, idx) => {
                                const tabIndex = idx + 1;
                                const isActive = activeQuantityTab === tabIndex;
                                return (
                                  <Button
                                    key={tabIndex}
                                    variant={isActive ? "default" : "outline"}
                                    size="xs"
                                    className={`h-7 px-3 text-xs ${isActive ? "" : "bg-background"}`}
                                    onClick={() => setActiveQuantityTab(tabIndex)}
                                  >
                                    Item {tabIndex}
                                  </Button>
                                );
                              })}
                            </div>
                          )}

                          {/* Active tab content */}
                          {(() => {
                            const qtyIndex = activeQuantityTab || 1;
                            const qtyState = perQuantitySelections[qtyIndex] || { selectedOptions: {}, optionQuantities: {} };
                            const qtySelections = qtyState.selectedOptions[dealItem.DealItemId] || [];

                            const currentCountForQty = Array.isArray(qtySelections) ? qtySelections.length : 0;

                            let totalQuantityForQty = 0;
                            if (Array.isArray(qtySelections)) {
                              qtySelections.forEach(opt => {
                                const qtyKey = `${dealItem.DealItemId}-${opt.id}`;
                                const qtyVal = qtyState.optionQuantities[qtyKey] || 1;
                                totalQuantityForQty += qtyVal;
                              });
                            }

                            const hasReachedMaxForQty = maxSelections === Infinity ? false : (totalQuantityForQty >= maxSelections);
                            const canSelectMoreItemsForQty = currentCountForQty < maxSelections && !hasReachedMaxForQty;

                            return (
                              <div className="space-y-2 border-t pt-2">
                                {quantity > 1 && (
                                  <p className="text-xs font-semibold text-muted-foreground">
                                    Selection for item {qtyIndex}
                                  </p>
                                )}
                                {descriptionsForThisItem.map(desc => {
                                  const isSelected = (qtySelections || []).some(
                                    opt => opt.id === desc.DealDescId
                                  );

                                  const optionQtyKey = `${dealItem.DealItemId}-${desc.DealDescId}`;
                                  const optionQty = qtyState.optionQuantities[optionQtyKey] || 1;

                                  const canSelectThisItem = !isSelected && canSelectMoreItemsForQty;

                                  return (
                                    <div
                                      key={desc.DealDescId}
                                      className={`flex items-center justify-between p-2 border rounded-lg hover:bg-muted/50 ${!canSelectThisItem && !isSelected ? 'opacity-50' : ''}`}>
                                      <div className="flex items-center space-x-2 flex-1">
                                        <input
                                          type="checkbox"
                                          id={`deal-${desc.DealDescId}-qty-${qtyIndex}`}
                                          checked={isSelected}
                                          onChange={(e) => {
                                            if (!canSelectThisItem && e.target.checked) return;
                                            handleCheckboxChange(qtyIndex, dealItem.DealItemId, desc.DealDescId, desc, e.target.checked);
                                          }}
                                          disabled={!canSelectThisItem && !isSelected}
                                          className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary accent-primary"
                                        />
                                        <Label
                                          htmlFor={`deal-${desc.DealDescId}-qty-${qtyIndex}`}
                                          className={`cursor-pointer flex-1 ${!canSelectThisItem && !isSelected ? 'cursor-not-allowed' : ''}`}>
                                          <div className="flex justify-between items-center">
                                            <span className="text-sm">{desc.ProductDetailName}</span>
                                            <span className="font-semibold text-sm">
                                              {desc.Price > 0 ? `+ Rs ${(desc.Price * optionQty).toFixed(2)}` : "Included"}
                                            </span>
                                          </div>
                                        </Label>
                                      </div>

                                      {/* Quantity counter for selected options in this quantity section */}
                                      {isSelected && (
                                        <div className="flex items-center space-x-2 ml-2">
                                          <Button
                                            variant="outline"
                                            size="icon"
                                            className="h-5 w-5"
                                            onClick={() => {
                                              if (optionQty > 1) {
                                                handleOptionQuantityChange(qtyIndex, dealItem.DealItemId, desc.DealDescId, optionQty - 1);
                                              }
                                            }}>
                                            <Minus className="h-3 w-3" />
                                          </Button>
                                          <span className="text-xs w-5 text-center">{optionQty}</span>
                                          <Button
                                            variant="outline"
                                            size="icon"
                                            className="h-5 w-5"
                                            onClick={() => {
                                              if (totalQuantityForQty >= maxSelections && maxSelections !== Infinity) return;
                                              handleOptionQuantityChange(qtyIndex, dealItem.DealItemId, desc.DealDescId, optionQty + 1);
                                            }}
                                            disabled={maxSelections !== Infinity && totalQuantityForQty >= maxSelections}>
                                            <Plus className="h-3 w-3" />
                                          </Button>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}

                                {hasReachedMaxForQty && maxSelections !== Infinity && (
                                  <p className="text-xs text-destructive mt-1">
                                    Maximum {maxSelections} items allowed for this quantity
                                  </p>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      )}

                      {/* Validation message */}
                      {minSelections > 0 && currentCount < minSelections && (
                        <p className="text-xs text-destructive">
                          Please select at least {minSelections} option(s)
                        </p>
                      )}

                      {/* Max quantity validation message */}
                      {hasReachedMaxQuantity && (
                        <p className="text-xs text-destructive">
                          Maximum {maxSelections} items allowed in total
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Fixed Bottom Section */}
        <div className="border-t px-4 py-2 bg-background sticky">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column - Special Instructions */}
            <div className="space-y-3 justify-end flex flex-col">
              <Label htmlFor="instructions" className="text-base font-medium">
                Special Instructions
              </Label>
              <Textarea
                id="instructions"
                placeholder="Any special requests or instructions..."
                value={specialInstructions}
                onChange={(e) => setSpecialInstructions(e.target.value)}
                className="min-h-[80px] text-sm"
              />
            </div>

            {/* Right Column - Quantity and Price */}
            <div className="space-y-4">
              {/* Quantity Selector */}
              <div className="flex items-center justify-between">
                <Label className="text-base font-medium">Quantity</Label>
                <div className="flex items-center space-x-3">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={decreaseQuantity}
                    disabled={quantity <= 1}
                    className="h-8 w-8">
                    <Minus className="h-3 w-3" />
                  </Button>
                  <span className="text-base font-semibold w-6 text-center">{quantity}</span>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={increaseQuantity}
                    className="h-8 w-8">
                    <Plus className="h-3 w-3" />
                  </Button>
                </div>
              </div>

              {/* Price Summary */}
              <div className="p-3 bg-muted/50 rounded-lg">
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>Base Price:</span>
                    <span>Rs {basePrice.toFixed(2)}</span>
                  </div>

                  {/* Show selected deal options and their prices */}
                  {Object.entries(selectedOptions).map(([dealItemId, option]) => {
                    if (Array.isArray(option)) {
                      return option.map((opt, optIndex) => {
                        const optionQty = optionQuantities[`${dealItemId}-${opt.id}`] || 1;
                        const optionTotal = (opt.price || 0) * optionQty;

                        return (
                          <div key={`${dealItemId}-${optIndex}`} className="flex justify-between ml-2">
                            <span>+ {opt.name} (x{optionQty}):</span>
                            <span>Rs {optionTotal.toFixed(2)}</span>
                          </div>
                        );
                      });
                    } else if (option && typeof option === 'object') {
                      return (
                        <div key={dealItemId} className="flex justify-between ml-2">
                          <span>+ {option.name}:</span>
                          <span>Rs {option.price.toFixed(2)}</span>
                        </div>
                      );
                    }
                    return null;
                  })}

                  {/* Show discount if applicable */}
                  {discountPercent > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Discount ({discountPercent}%):</span>
                      <span>- Rs {discountAmount.toFixed(2)}</span>
                    </div>
                  )}

                  <div className="flex justify-between font-semibold border-t pt-1 mt-1">
                    <span>Total:</span>
                    <span>Rs {totalPrice.toFixed(2)}</span>
                  </div>
                </div>
              </div>

            </div>
          </div>
          {/* Add to Cart Button */}
          <Button
            onClick={handleAddToCart}
            // disabled={(!selectedVariant && !product.IsDeal) || !isDealSelectionValid()}
            disabled={!isDealSelectionValid()}
            className="w-full py-2 mt-3 text-base"
            size="lg">
            Add {quantity} to Cart - Rs {totalPrice.toFixed(2)}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}